import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { ApiError } from '@/types'
import { createMockAdapter } from '@/lib/devMock'
import { friendlyMessage } from '@/lib/errorMessages'
import { useDelegationStore } from '@/store/delegation'

// ── Axios instances ────────────────────────────────────────────────────────────

const BASE_URL = import.meta.env.VITE_API_BASE_URL as string

// Dev mock — activated by VITE_MOCK=true in .env
const mockAdapter = import.meta.env.VITE_MOCK === 'true' ? createMockAdapter() : undefined

/** Primary API client — all authenticated requests go through this instance. */
const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  ...(mockAdapter ? { adapter: mockAdapter } : {}),
})

/**
 * Bare instance used only for the token-refresh call.
 * Must NOT share interceptors with `api` to avoid refresh loops.
 */
const refreshApi = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  ...(mockAdapter ? { adapter: mockAdapter } : {}),
})

// ── Helpers ────────────────────────────────────────────────────────────────────

interface RawApiError {
  success: false
  error: { code: string; message: string; data?: unknown }
}

interface TokenResponse {
  access_token: string
  refresh_token: string
  expires_in: number
}

function toApiError(err: AxiosError): ApiError {
  if (!err.response) {
    return new ApiError('NETWORK_ERROR', 'Cannot reach the server. Check your connection.')
  }
  const body = err.response.data as RawApiError | undefined
  const code    = body?.error?.code    ?? 'UNKNOWN'
  const rawMsg  = body?.error?.message ?? ''
  const message = friendlyMessage(code, rawMsg)
  return new ApiError(code, message, err.response.status, body?.error?.data)
}

function clearSession(): void {
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('token_expires_at')
  window.dispatchEvent(new CustomEvent('auth:session-expired'))
}

// ── Proactive refresh timer ────────────────────────────────────────────────────

let proactiveTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Stores both tokens and schedules a proactive refresh 60 s before expiry.
 * Called after every successful login or refresh.
 */
export function storeTokens(tokens: TokenResponse): void {
  localStorage.setItem('access_token', tokens.access_token)
  localStorage.setItem('refresh_token', tokens.refresh_token)

  if (tokens.expires_in > 0) {
    const expiresAt = Date.now() + tokens.expires_in * 1000
    localStorage.setItem('token_expires_at', String(expiresAt))
    scheduleProactiveRefresh(tokens.expires_in)
  }
}

function scheduleProactiveRefresh(expiresIn: number): void {
  if (proactiveTimer) clearTimeout(proactiveTimer)
  // Refresh 60 s before expiry (minimum 5 s delay to avoid tight loops)
  const delay = Math.max(5_000, (expiresIn - 60) * 1000)
  proactiveTimer = setTimeout(doProactiveRefresh, delay)
}

async function doProactiveRefresh(): Promise<void> {
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) return
  try {
    const tokens = await callRefresh(refreshToken)
    storeTokens(tokens)
    window.dispatchEvent(new CustomEvent('auth:token-refreshed'))
  } catch {
    // Proactive refresh failed — reactive interceptor will handle the next 401
  }
}

/** Initialise the proactive timer from localStorage on page load. */
export function initTokenRefreshTimer(): void {
  const expiresAt = Number(localStorage.getItem('token_expires_at') ?? 0)
  if (!expiresAt) return
  const remaining = Math.floor((expiresAt - Date.now()) / 1000)
  if (remaining > 5) {
    scheduleProactiveRefresh(remaining)
  } else if (remaining > 0) {
    // About to expire — refresh immediately
    void doProactiveRefresh()
  }
  // If already expired the reactive interceptor handles it on next request
}

// ── Concurrent-refresh queue ───────────────────────────────────────────────────

let isRefreshing = false
let refreshQueue: Array<(token: string) => void> = []

function enqueueAfterRefresh(cb: (token: string) => void): void {
  refreshQueue.push(cb)
}

function flushRefreshQueue(token: string): void {
  refreshQueue.forEach((cb) => cb(token))
  refreshQueue = []
}

// ── Shared refresh call (handles both bare and wrapped response shapes) ────────

async function callRefresh(refreshToken: string): Promise<TokenResponse> {
  const res = await refreshApi.post<unknown>('/auth/refresh', { refresh_token: refreshToken })
  // API may return bare { access_token, ... } or wrapped { success, data: { ... } }
  const body = res.data as Record<string, unknown>
  const tokens: TokenResponse =
    typeof body.access_token === 'string'
      ? (body as unknown as TokenResponse)
      : (body.data as TokenResponse)

  if (!tokens?.access_token) throw new Error('Invalid refresh response')
  return tokens
}

// ── Request interceptor: attach Bearer token + X-Act-As delegation header ─────

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }

  // Inject delegation header when acting on behalf of another user.
  const actingAsUserId = useDelegationStore.getState().actingAsUserId
  if (actingAsUserId) {
    config.headers['X-Act-As'] = actingAsUserId
  }

  // Let Axios set Content-Type automatically for FormData (multipart/form-data + boundary)
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }

  return config
})

// ── Response interceptor: normalise errors + silent token refresh ──────────────

type RetryableConfig = InternalAxiosRequestConfig & { _retried?: boolean }

// Auth endpoints that must never trigger the silent-refresh retry loop
const AUTH_ENDPOINTS = ['/auth/login', '/auth/refresh', '/auth/logout']

api.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const original = err.config as RetryableConfig | undefined

    // Skip retry for auth endpoints
    const url = original?.url ?? ''
    const isAuthEndpoint = AUTH_ENDPOINTS.some((p) => url.endsWith(p))

    if (err.response?.status === 401 && original && !original._retried && !isAuthEndpoint) {
      original._retried = true
      const refreshToken = localStorage.getItem('refresh_token')

      if (!refreshToken) {
        clearSession()
        return Promise.reject(toApiError(err))
      }

      // If a refresh is already in-flight, queue this request until it resolves
      if (isRefreshing) {
        return new Promise((resolve) => {
          enqueueAfterRefresh((newToken) => {
            original.headers['Authorization'] = `Bearer ${newToken}`
            resolve(api(original))
          })
        })
      }

      isRefreshing = true

      try {
        const tokens = await callRefresh(refreshToken)
        storeTokens(tokens)
        window.dispatchEvent(new CustomEvent('auth:token-refreshed'))

        isRefreshing = false
        flushRefreshQueue(tokens.access_token)

        original.headers['Authorization'] = `Bearer ${tokens.access_token}`
        return api(original)
      } catch {
        isRefreshing = false
        refreshQueue = []
        clearSession()
        return Promise.reject(toApiError(err))
      }
    }

    return Promise.reject(toApiError(err))
  }
)

export default api
